document.addEventListener("DOMContentLoaded", function () {
  initBarCharts();
});

/**
 * Main initialization function for bar charts
 * Creates charts with IDs for:
 * - Method charts: googleChartWireless, googleChartWired, googleChartVOIP
 * - Type charts: googleChartTypeBarPrerecordedVoice, googleChartTypeBarLiveVoice, etc.
 * - Issue charts: googleChartIssueBarUnwantedCalls, googleChartIssueBarTelemarketing, etc.
 */
function initBarCharts() {
  // Store chart instances for resize handling
  const chartInstances = [];

  google.charts.load("current", { packages: ["corechart"] });
  google.charts.setOnLoadCallback(() => {
    drawScamMethodBarCharts();
    drawScamTypeBarCharts();
    drawScamIssueBarCharts();

    // Add window resize handler
    window.addEventListener("resize", function () {
      clearTimeout(window.barChartsResizeTimer);
      window.barChartsResizeTimer = setTimeout(() => {
        // Clear and redraw all charts
        chartInstances.forEach((chart) => {
          if (chart && typeof chart.clearChart === "function") {
            chart.clearChart();
            chart.draw(chart.data, chart.options);
          }
        });
      }, 100);
    });
  });

  /**
   * Helper function to get chart options
   */
  function getBarChartOptions(title, color) {
    const isMobile = window.innerWidth < 768;
    const isSmallScreen = window.innerWidth < 992; // eslint-disable-line no-unused-vars

    // Adjust chart dimensions for mobile
    const leftPadding = isMobile ? 35 : 50;
    const rightPadding = isMobile ? 5 : 10;
    const fontSize = isMobile ? 8 : 10;
    const titleFontSize = isMobile ? 14 : 18;

    return {
      title: title,
      titleTextStyle: {
        fontSize: titleFontSize,
        bold: true,
        color: color,
      },
        alignment: "center",
      legend: { position: "none" },
      colors: [color],
      chartArea: {
        left: leftPadding,
        right: rightPadding,
        top: 40,
        bottom: isMobile ? 50 : 70,
        width: "90%",
        height: "75%",
      },
      hAxis: {
        title: "Year",
        slantedText: true,
        slantedTextAngle: 45,
        showTextEvery: 1,
        textStyle: { fontSize: fontSize },
        format: "####", // Force full year display
      },
      vAxis: {
        title: "Complaints",
        textStyle: { fontSize: fontSize },
        gridlines: { count: isMobile ? 4 : 6 },
      },
      width: "100%",
      height: "100%",
      fontSize: fontSize,
      bar: { groupWidth: "70%" },
    };
  }

  /**
   * Creates bar charts for scam method data
   * HTML Element IDs used:
   * - googleChartWireless: Displays Wireless/Mobile complaints
   * - googleChartWired: Displays Wired complaints
   * - googleChartVOIP: Displays VOIP complaints
   */
  function drawScamMethodBarCharts() {
    // Check if method data exists
    if (
      !apiData.flourishScamSection ||
      !apiData.flourishScamSection.byMethod ||
      !apiData.flourishScamSection.byMethod.data ||
      !apiData.flourishScamSection.byMethod.data.data
    ) {
      return;
    }

    try {
      const rawData = apiData.flourishScamSection.byMethod.data.data;
      const years = rawData.slice(1).map((row) => row[0]);

      // Create wireless data
      const wirelessData = new google.visualization.DataTable();
      wirelessData.addColumn("string", "Year");
      wirelessData.addColumn("number", "Wireless/Mobile");
      years.forEach((year, i) => {
        wirelessData.addRow([year, parseInt(rawData[i + 1][1] || 0)]);
      });

      // Create wired data
      const wiredData = new google.visualization.DataTable();
      wiredData.addColumn("string", "Year");
      wiredData.addColumn("number", "Wired");
      years.forEach((year, i) => {
        wiredData.addRow([year, parseInt(rawData[i + 1][2] || 0)]);
      });

      // Create VOIP data
      const voipData = new google.visualization.DataTable();
      voipData.addColumn("string", "Year");
      voipData.addColumn("number", "VOIP");
      years.forEach((year, i) => {
        voipData.addRow([year, parseInt(rawData[i + 1][3] || 0)]);
      });

      // Draw charts in their respective containers
      const wirelessElement = document.getElementById("googleChartWireless");
      const wiredElement = document.getElementById("googleChartWired");
      const voipElement = document.getElementById("googleChartVOIP");

      if (wirelessElement) {
        const options = getBarChartOptions("Wireless/Mobile", "#2e3192");
        const chart = new google.visualization.ColumnChart(wirelessElement);
        chart.draw(wirelessData, options);
        chart.data = wirelessData;
        chart.options = options;
        chartInstances.push(chart);
      }

      if (wiredElement) {
        const options = getBarChartOptions("Wired", "#ef877a");
        const chart = new google.visualization.ColumnChart(wiredElement);
        chart.draw(wiredData, options);
        chart.data = wiredData;
        chart.options = options;
        chartInstances.push(chart);
      }

      if (voipElement) {
        const options = getBarChartOptions("VOIP", "#83c88b");
        const chart = new google.visualization.ColumnChart(voipElement);
        chart.draw(voipData, options);
        chart.data = voipData;
        chart.options = options;
        chartInstances.push(chart);
      }
    } catch (error) {
      console.error("Error creating method charts:", error);
    }
  }

  /**
   * Creates bar charts for scam type data
   * HTML Element IDs used:
   * - googleChartTypeBarPrerecordedVoice: Pre-recorded Voice complaints
   * - googleChartTypeBarLiveVoice: Live Voice complaints
   * - googleChartTypeBarAbandonedCalls: Abandoned Calls complaints
   * - googleChartTypeBarTextMessage: Text Message complaints
   */
  function drawScamTypeBarCharts() {
    // Check if type data exists
    if (
      !apiData.flourishScamSection ||
      !apiData.flourishScamSection.byType ||
      !apiData.flourishScamSection.byType.data ||
      !apiData.flourishScamSection.byType.data.data
    ) {
      return;
    }

    try {
      const rawData = apiData.flourishScamSection.byType.data.data;
      const years = rawData.slice(1).map((row) => row[0]);
      const typeLabels = [
        "Pre-recorded Voice",
        "Live Voice",
        "Abandoned Calls",
        "Text Message",
      ];
      const colors = ["#2e3192", "#ef877a", "#83c88b", "#936fbe"];

      typeLabels.forEach((label, index) => {
        const data = new google.visualization.DataTable();
        data.addColumn("string", "Year");
        data.addColumn("number", label);

        years.forEach((year, i) => {
          // Add fallback to 0 if data is missing
          data.addRow([year, parseInt(rawData[i + 1][index + 1] || 0)]);
        });

        const chartId = `googleChartTypeBar${label.replace(/[^a-zA-Z]/g, "")}`;
        const chartElement = document.getElementById(chartId);

        if (chartElement) {
          const options = getBarChartOptions(`${label}`, colors[index]);
          const chart = new google.visualization.ColumnChart(chartElement);
          chart.draw(data, options);
          chart.data = data;
          chart.options = options;
          chartInstances.push(chart);
        }
      });
    } catch (error) {
      console.error("Error creating type charts:", error);
    }
  }

  /**
   * Creates bar charts for scam issue data
   * HTML Element IDs used:
   * - googleChartIssueBarUnwantedCalls: Unwanted Calls complaints
   * - googleChartIssueBarTelemarketing: Telemarketing complaints
   * - googleChartIssueBarRobocalls: Robocalls complaints
   */
  function drawScamIssueBarCharts() {
    // Check if issue data exists
    if (
      !apiData.flourishScamSection ||
      !apiData.flourishScamSection.byIssue ||
      !apiData.flourishScamSection.byIssue.data ||
      !apiData.flourishScamSection.byIssue.data.data
    ) {
      return;
    }

    try {
      const rawData = apiData.flourishScamSection.byIssue.data.data;
      const years = rawData.slice(1).map((row) => row[0]);
      const issueLabels = ["Unwanted Calls", "Telemarketing", "Robocalls"];
      const colors = ["#2e3192", "#ef877a", "#83c88b"];

      issueLabels.forEach((label, index) => {
        const data = new google.visualization.DataTable();
        data.addColumn("string", "Year");
        data.addColumn("number", label);

        years.forEach((year, i) => {
          // Add fallback to 0 if data is missing
          data.addRow([year, parseInt(rawData[i + 1][index + 1] || 0)]);
        });

        const chartId = `googleChartIssueBar${label.replace(/[^a-zA-Z]/g, "")}`;
        const chartElement = document.getElementById(chartId);

        if (chartElement) {
          const options = getBarChartOptions(`${label}`, colors[index]);
          const chart = new google.visualization.ColumnChart(chartElement);
          chart.draw(data, options);
          chart.data = data;
          chart.options = options;
          chartInstances.push(chart);
        }
      });
    } catch (error) {
      console.error("Error creating issue charts:", error);
    }
  }
}

export default initBarCharts;
